import api from "../services/apiService";
import { fetchContactQueries, fetchExpertRequests } from "./websiteController";
import { RECORD_CATEGORIES } from "../constants";
import { getUserProfile } from "./utilityController";
import { KPI_DEFINITIONS as ACTIVITY_METRICS } from "../components/project-management/ActivityLogTab";
import {
  mapApiProjectToRecord,
  mapProjectRecord,
} from "../pages/project-management/projectMockData";

// How far back the "new" website-lead counts look.
export const RECENT_DAYS = 7;
// Page size used to count recent website leads. If every row on the page is
// recent, the count is reported as a lower bound ("100+").
const RECENT_SAMPLE = 100;

const toList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const DAY_MS = 24 * 60 * 60 * 1000;
// A project is "behind" when its recruitment % trails the share of its
// timeline already used by more than this many percentage points.
const BEHIND_TOLERANCE = 10;
const DUE_SOON_DAYS = 7;

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Date-only strings ("2026-10-01") are parsed as local days, not UTC midnight.
const parseDay = (value) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysBetween = (from, to) => Math.round((to - from) / DAY_MS);

const isActiveProject = (project) =>
  String(project?.status || "").toLowerCase() === "active";

/**
 * Why an active project needs attention, or null when it's on track.
 * Projects that have already hit their target are never flagged.
 * @returns {{ kind: "overdue"|"dueSoon"|"behind", label: string, rank: number } | null}
 */
const getAttentionReason = (project, today) => {
  if (project.percentage >= 100) return null;

  const due = parseDay(project.dueDate);
  if (due) {
    const daysLeft = daysBetween(today, due);
    if (daysLeft < 0) {
      const late = -daysLeft;
      // Rank: most overdue first.
      return { kind: "overdue", label: `Overdue by ${late} day${late === 1 ? "" : "s"}`, rank: 3000 + late };
    }
    if (daysLeft <= DUE_SOON_DAYS) {
      const label =
        daysLeft === 0 ? "Due today" : `Due in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
      return { kind: "dueSoon", label, rank: 2000 + (DUE_SOON_DAYS - daysLeft) };
    }
  }

  const start = parseDay(project.startDate);
  if (start && due && due > start && start <= today) {
    const elapsed = Math.round((daysBetween(start, today) / daysBetween(start, due)) * 100);
    const gap = elapsed - project.percentage;
    if (gap > BEHIND_TOLERANCE) {
      return { kind: "behind", label: `Behind: ${elapsed}% of time used`, rank: 1000 + gap };
    }
  }

  return null;
};

// Counts active projects per value of `field`, largest first. Blank values
// are grouped under `fallback`.
const countBy = (projects, field, fallback) => {
  const counts = projects.reduce((acc, p) => {
    const key = String(p?.[field] || "").trim() || fallback;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
};

// Active projects, mapped to the record shape the Project Management page uses
// so they can be handed to the project board as navigation state.
const fetchActiveProjects = async () => {
  const res = await api.get("/projects");
  return toList(res?.data)
    .filter(isActiveProject)
    .map((p) => mapProjectRecord(mapApiProjectToRecord(p)));
};

const byUrgency = (a, b) => {
  const rank = (p) => p.reason?.rank ?? 0;
  if (rank(b) !== rank(a)) return rank(b) - rank(a);
  const due = (p) => parseDay(p.dueDate)?.getTime() ?? Infinity;
  return due(a) - due(b);
};

/**
 * KPIs, the "needs attention" list and the priority/type mix for a set of
 * active projects. `projects` holds every project with its attention reason
 * (null when on track), most urgent first.
 */
const summariseProjects = (active) => {
  const today = startOfToday();
  let recruited = 0;
  let targets = 0;
  const projects = active.map((project) => {
    recruited += Number(project.totalRecruitsCount) || 0;
    targets += Number(project.totalTasks) || 0;
    return {
      ...project,
      id: project.backendId ?? project.projectId,
      reason: getAttentionReason(project, today),
    };
  });
  projects.sort(byUrgency);
  const attention = projects.filter((p) => p.reason);

  return {
    active: active.length,
    overdue: attention.filter((p) => p.reason.kind === "overdue").length,
    recruited,
    targets,
    projects,
    attention,
    byPriority: countBy(active, "priority", "Unset"),
    byType: countBy(active, "projectType", "General"),
  };
};

const fetchProjectKpis = async () => summariseProjects(await fetchActiveProjects());

// `/filter` returns `total` for the category; one row is enough to read it.
const fetchRecordTotal = async (categoryId) => {
  const { data } = await api.get("/filter", {
    params: { page: 1, limit: 1, categoryId },
  });
  return Number(data?.total) || 0;
};

export const fetchRecordKpis = async () => {
  const [warm, cold] = await Promise.all([
    fetchRecordTotal(RECORD_CATEGORIES.WARM),
    fetchRecordTotal(RECORD_CATEGORIES.COLD),
  ]);
  return { warm, cold, total: warm + cold };
};

const countRecent = (rows) => {
  const since = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
  return rows.filter((r) => {
    const t = new Date(r?.createdAt).getTime();
    return !Number.isNaN(t) && t >= since;
  }).length;
};

const createdAtMs = (row) => {
  const t = new Date(row?.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// `latestCount` newest rows are kept for the dashboard's lead lists. They're
// sorted here because the API's default ordering isn't guaranteed.
const summariseLeads = (res, latestCount) => {
  const rows = toList(res);
  const recent = countRecent(rows);
  return {
    recent,
    recentIsLowerBound: rows.length >= RECENT_SAMPLE && recent === rows.length,
    total: Number(res?.pagination?.total) || rows.length,
    latest: [...rows].sort((a, b) => createdAtMs(b) - createdAtMs(a)).slice(0, latestCount),
  };
};

const fetchExpertKpis = async () =>
  summariseLeads(await fetchExpertRequests(1, RECENT_SAMPLE), 3);

const fetchContactKpis = async () =>
  summariseLeads(await fetchContactQueries(1, RECENT_SAMPLE), 3);

const PERFORMANCE_TOP = 5;

/**
 * Leaderboard across all projects. The API returns rows already ranked
 * (screener responses, then conversion rate), so order is preserved.
 */
const fetchPerformanceKpis = async () => {
  const { data } = await api.get("/performance-summary");
  const rows = Array.isArray(data) ? data : [];
  const byStatus = rows.reduce((acc, r) => {
    const key = r?.status || "Unrated";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const ranked = rows.map((r, i) => ({ ...r, rank: i + 1 }));
  const totalOutput = rows.reduce((sum, r) => sum + (Number(r?.totalOutput) || 0), 0);
  const totalEffort = rows.reduce((sum, r) => sum + (Number(r?.totalEffort) || 0), 0);
  return {
    total: rows.length,
    byStatus,
    rows: ranked,
    top: ranked.slice(0, PERFORMANCE_TOP),
    totalOutput,
    totalEffort,
    // Team-wide conversion (output / effort), as a percentage.
    teamConversion: totalEffort ? (totalOutput / totalEffort) * 100 : 0,
  };
};

const fetchHrKpis = async () => {
  const { data } = await api.get("/hr/dashboard");
  return {
    total: Number(data?.employees?.total) || 0,
    active: Number(data?.employees?.byStatus?.active) || 0,
    docsExpiring: Number(data?.documents?.expiring) || 0,
  };
};

/**
 * Loads every admin KPI in parallel. Each key holds `{ data }` on success or
 * `{ error }` on failure, so one failing endpoint doesn't blank the others.
 */
export const fetchAdminKpis = async () => {
  const sources = {
    projects: fetchProjectKpis,
    records: fetchRecordKpis,
    experts: fetchExpertKpis,
    contacts: fetchContactKpis,
    hr: fetchHrKpis,
    performance: fetchPerformanceKpis,
  };
  const keys = Object.keys(sources);
  const results = await Promise.allSettled(keys.map((k) => sources[k]()));

  return keys.reduce((acc, key, i) => {
    const r = results[i];
    acc[key] =
      r.status === "fulfilled" ? { data: r.value } : { error: r.reason };
    return acc;
  }, {});
};

// ── Employee & manager dashboards ────────────────────────────────────────────

// Screener responses are "output"; every other logged metric is "effort".
const OUTPUT_METRIC = "screenerResponsesReceived";
const EFFORT_METRICS = ACTIVITY_METRICS.map((m) => m.key).filter((k) => k !== OUTPUT_METRIC);
export const TREND_DAYS = 7;
// Activity logs are fetched per project, so the number of calls is capped.
export const MAX_LOG_PROJECTS = 15;

// Reads the JWT payload for identity fields the profile endpoint may not return.
const readTokenClaims = () => {
  try {
    const payload = localStorage.getItem("token").split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
};

const normEmail = (email) => String(email || "").trim().toLowerCase();

/** The signed-in user's name, role, id and email (id/email may be null). */
export const getCurrentUser = async () => {
  const profile = await getUserProfile();
  const claims = readTokenClaims();
  return {
    name: profile?.name || "",
    role: profile?.role || "",
    id: profile?.id ?? profile?.userId ?? claims.id ?? claims.userId ?? null,
    email: normEmail(profile?.email ?? claims.email) || null,
  };
};

const matchesUser = (me, { id, email }) =>
  (me.id != null && id != null && String(me.id) === String(id)) ||
  (Boolean(me.email) && normEmail(email) === me.email);

const dayKey = (date) => {
  const d = parseDay(date);
  if (!d) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const logEffort = (log) => EFFORT_METRICS.reduce((sum, k) => sum + (Number(log?.[k]) || 0), 0);

/**
 * Activity logs from the last TREND_DAYS days across `projects` (capped at
 * MAX_LOG_PROJECTS), newest first. Each log carries its project record.
 */
const fetchRecentLogs = async (projects) => {
  const scanned = projects.slice(0, MAX_LOG_PROJECTS);
  const results = await Promise.allSettled(
    scanned.map((project) =>
      api.get("/activity-logs", { params: { projectId: project.backendId } }).then((res) =>
        (Array.isArray(res?.data) ? res.data : []).map((log) => ({ ...log, project })),
      ),
    ),
  );
  const since = startOfToday();
  since.setDate(since.getDate() - (TREND_DAYS - 1));
  const logs = results
    .filter((r) => r.status === "fulfilled")
    .flatMap((r) => r.value)
    .filter((log) => {
      const d = parseDay(log.date);
      return d && d >= since;
    })
    .sort(
      (a, b) =>
        parseDay(b.date) - parseDay(a.date) || (Number(b.id) || 0) - (Number(a.id) || 0),
    );

  return {
    logs,
    skippedProjects: projects.length - scanned.length,
    failedProjects: results.filter((r) => r.status === "rejected").length,
  };
};

/** One entry per day for the last TREND_DAYS days, oldest first. */
const dailySeries = (logs, valueOf) => {
  const today = startOfToday();
  return Array.from({ length: TREND_DAYS }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (TREND_DAYS - 1 - i));
    const key = dayKey(d);
    return {
      key,
      label: d.toLocaleDateString("en-US", { weekday: "short" }),
      fullLabel: d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }),
      value: valueOf(logs.filter((log) => dayKey(log.date) === key)),
      isToday: i === TREND_DAYS - 1,
    };
  });
};

const metricTotals = (logs) =>
  ACTIVITY_METRICS.map(({ key, label }) => ({
    key,
    label,
    value: logs.reduce((sum, log) => sum + (Number(log?.[key]) || 0), 0),
  }));

// Runs each loader in parallel; a failing loader only marks its own key.
const settle = async (sources) => {
  const keys = Object.keys(sources);
  const results = await Promise.allSettled(keys.map((k) => sources[k]()));
  return keys.reduce((acc, key, i) => {
    const r = results[i];
    acc[key] = r.status === "fulfilled" ? { data: r.value } : { error: r.reason };
    return acc;
  }, {});
};

/**
 * Personal dashboard for the "user" role: the projects they're assigned to,
 * their leaderboard row and their own activity logs. If the user's email
 * can't be determined, every visible project is used instead.
 */
export const fetchUserDashboard = async (me) => {
  const projectsPromise = fetchActiveProjects().then((active) => {
    const mine = me.email
      ? active.filter((p) => (p.assignedUsers || []).some((e) => normEmail(e) === me.email))
      : active;
    return { ...summariseProjects(mine), scopedToUser: Boolean(me.email) };
  });
  // Avoid an unhandled rejection before `settle` attaches its handler.
  projectsPromise.catch(() => {});

  const loadMyPerformance = async () => {
    const perf = await fetchPerformanceKpis();
    const mine = perf.rows.find((r) => matchesUser(me, { id: r.userId, email: r.user?.email }));
    return { me: mine || null, rankedCount: perf.total, teamConversion: perf.teamConversion };
  };

  const loadMyActivity = async () => {
    const { projects } = await projectsPromise;
    const { logs, skippedProjects, failedProjects } = await fetchRecentLogs(projects);
    const mine = logs.filter((log) =>
      matchesUser(me, { id: log.userId ?? log.User?.id, email: log.User?.email }),
    );
    const todayKey = dayKey(startOfToday());
    return {
      daily: dailySeries(mine, (dayLogs) => dayLogs.reduce((sum, l) => sum + logEffort(l), 0)),
      totals: metricTotals(mine),
      daysLogged: new Set(mine.map((l) => dayKey(l.date))).size,
      loggedToday: mine.some((l) => dayKey(l.date) === todayKey),
      recent: mine.slice(0, 5),
      skippedProjects,
      failedProjects,
    };
  };

  return settle({
    projects: () => projectsPromise,
    performance: loadMyPerformance,
    activity: loadMyActivity,
  });
};

/**
 * Team dashboard for the "manager" role: every visible project, the full
 * leaderboard, record totals and the team's activity logs.
 */
export const fetchManagerDashboard = async () => {
  const activePromise = fetchActiveProjects();
  activePromise.catch(() => {});

  const loadTeamActivity = async () => {
    const active = await activePromise;
    const { logs, skippedProjects, failedProjects } = await fetchRecentLogs(active);
    const todayKey = dayKey(startOfToday());

    // Everyone assigned to an active project is expected to log activity.
    const assignees = [
      ...new Set(active.flatMap((p) => (p.assignedUsers || []).map(normEmail)).filter(Boolean)),
    ];
    const loggedToday = new Set(
      logs.filter((l) => dayKey(l.date) === todayKey).map((l) => normEmail(l.User?.email)),
    );

    return {
      daily: dailySeries(logs, (dayLogs) => dayLogs.length),
      totals: metricTotals(logs),
      assigneeCount: assignees.length,
      loggedTodayCount: assignees.filter((e) => loggedToday.has(e)).length,
      notLoggedToday: assignees
        .filter((e) => !loggedToday.has(e))
        .sort()
        .map((email) => ({
          email,
          projects: active.filter((p) =>
            (p.assignedUsers || []).some((e) => normEmail(e) === email),
          ),
        })),
      recent: logs.slice(0, 6),
      skippedProjects,
      failedProjects,
    };
  };

  return settle({
    projects: async () => summariseProjects(await activePromise),
    records: fetchRecordKpis,
    performance: fetchPerformanceKpis,
    activity: loadTeamActivity,
  });
};
